import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import logger from '../utils/logger';
import { API_URL } from '../utils/apiConfig';

const AuthContext = createContext();

// Configure axios to always send cookies
axios.defaults.withCredentials = true;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`);
    } catch (error) {
      logger.error('Logout API call failed:', error);
    }
    setUser(null);
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      // Cookie absent ou expiré → pas connecté
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password, rememberMe = false) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { 
      email, 
      password,
      remember_me: rememberMe
    });
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, companyName, language, consentAccepted) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, {
      email, password, name, company_name: companyName, language,
      consent_accepted: consentAccepted,
    });
    return response.data;
  };

  const resendVerificationEmail = async (email) => {
    const response = await axios.post(`${API_URL}/api/auth/resend-verification`, { email });
    return response.data;
  };

  const impersonate = async (userId, reason) => {
    const response = await axios.post(
      `${API_URL}/api/admin/impersonate/${userId}`,
      { reason }
    );
    setUser(response.data.user);
    return response.data.user;
  };

  const exitImpersonation = async () => {
    const response = await axios.post(`${API_URL}/api/admin/exit-impersonation`);
    setUser(response.data.user);
    return response.data.user;
  };

  const updateLanguage = async (language) => {
    await axios.put(`${API_URL}/api/auth/me`, { language });
    setUser(prev => ({ ...prev, language }));
  };

  // RGPD-2.3 — Self soft-delete (Art. 17 RGPD).
  // Sur succès, le backend clear le cookie httpOnly + retourne summary.
  // Le caller doit ensuite logout côté state (setUser(null)) + redirect.
  const deleteAccount = async (confirmationEmail, password) => {
    const response = await axios.delete(`${API_URL}/api/auth/me`, {
      data: { confirmation_email: confirmationEmail, password },
    });
    return response.data;
  };

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      loading,
      login,
      register,
      resendVerificationEmail,
      impersonate,
      exitImpersonation,
      logout,
      updateLanguage,
      deleteAccount,
      isAuthenticated: !!user,
      isCsAdmin: !!user?.cs_admin,
      isCompanyAdmin: user?.role === 'admin',
      isCompanyMember: !!user?.company_id,
      isImpersonating: !!user?.is_impersonating,
      originalUserId: user?.original_user_id || null,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
